import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ChamadaEtapaPerfilService } from '../../shared/services/chamada-etapa-perfil.service';
import { EtapaEnum } from '../../core/enums/etapa.enum';
import { FaseEnum } from '../../core/enums/fase.enum';
import { LoadingService } from '../../shared/services/loading.service';
import { BrSelectCustomComponent } from '../../shared/components/br-select-custom/br-select-custom.component';
import { SelectOption } from '../../shared/model/select-options.model';
import { CommonModule } from '@angular/common';
import { Ciclo } from '../../shared/models/response/ciclo.model';
import { AlertService } from '../../shared/services/alert.service';
import { DateUtil } from '../../shared/utils/date.util';
import { UsuarioResponseDto } from '../../shared/models/response/usuario-response.model';
import { Municipio, Uf } from '../../shared/models/response/localidade.model';
import { PerfilEnum } from '../../core/enums/perfil.enum';
import { parse } from 'date-fns';
import { ConfirmacaoVagaTipoEquipeComponent } from './tipo-equipe/confirmacao-vaga-tipo-equipe.component';
import { ConfirmacaoVagaAprimoramentoComponent } from './aprimoramento/confirmacao-vaga-aprimoramento.component';

@Component({
  selector: 'app-confirmacao-vaga',
  templateUrl: './confirmacao-vaga.component.html',
  styleUrls: ['./confirmacao-vaga.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    BrSelectCustomComponent,
    ConfirmacaoVagaTipoEquipeComponent,
    ConfirmacaoVagaAprimoramentoComponent,
  ],
  standalone: true,
})
export class ConfirmacaoVagaComponent implements OnInit {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly alertService = inject(AlertService);
  private readonly loadingService = inject(LoadingService);
  private readonly chamadaEtapaPerfilService = inject(ChamadaEtapaPerfilService);

  form!: FormGroup;
  usuario!: UsuarioResponseDto;
  municipio!: Municipio | null;
  uf!: Uf | null;

  ciclos: any[] = [];
  allChamadaEtapaPerfil: any[] = [];

  comboProgramas = signal<SelectOption[]>([{ value: '', label: 'Selecione', selected: true }]);
  comboCiclos = signal<SelectOption[]>([{ value: '', label: 'Selecione', selected: true }]);

  selectedProgramaId: number | null = null;
  tipoProvisionamento: number | null = null;
  cicloSelected: Ciclo | null = null;

  ngOnInit(): void {
    this.buildForm();
    this.activatedRoute.data.subscribe((data) => {
      this.usuario = data['usuario'];
      this.municipio = this.usuario.relPerfis.find(
        (perfil) =>
          perfil.perfil.id === PerfilEnum.GESTOR_MUNICIPAL ||
          perfil.perfil.id === PerfilEnum.USUARIO_MUNICIPAL,
      )?.municipio as Municipio;
      if (!this.municipio) {
        this.uf = this.usuario.relPerfis.find(
          (perfil) => perfil.perfil.id === PerfilEnum.GESTOR_ESTADUAL,
        )?.uf as Uf;
      }
    });
    this.loadCiclos();
  }

  onChangePrograma(programaId: any): void {
    if (!programaId) {
      this.selectedProgramaId = null;
      this.tipoProvisionamento = null;
      this.cicloSelected = null;
      this.comboCiclos.set([{ value: '', label: 'Selecione', selected: true }]);
      this.form.get('ciclo')?.setValue(null);
      this.cdr.detectChanges();
      return;
    }

    this.selectedProgramaId = +programaId;
    const filtered = this.allChamadaEtapaPerfil.filter(
      (cep) => cep.cicloFase.ciclo.programa.id === +programaId,
    );

    const programa = filtered[0]?.cicloFase?.ciclo?.programa;
    this.tipoProvisionamento = programa?.tipoProvisionamento?.id ?? null;

    const cicloOptions: SelectOption[] = filtered.map((cep) => ({
      label: `${cep.cicloFase.ciclo.numeroCiclo}/${cep.cicloFase.ciclo.anoCiclo}`,
      value: String(cep.cicloFase.ciclo.id),
      selected: false,
    }));

    const unique = cicloOptions.filter(
      (opt, idx, self) => self.findIndex((o) => o.value === opt.value) === idx,
    );

    this.comboCiclos.set(
      unique.length === 1
        ? unique
        : [{ value: '', label: 'Selecione', selected: true }, ...unique],
    );

    this.cicloSelected = null;
    this.form.get('ciclo')?.setValue(null);
    this.cdr.detectChanges();
  }

  onChangeCiclo(cicloId: any): void {
    if (!cicloId) {
      this.cicloSelected = null;
      this.cdr.detectChanges();
      return;
    }

    const cep = this.allChamadaEtapaPerfil.find(
      (c) => c.cicloFase.ciclo.id === +cicloId,
    );
    if (!cep) return;

    this.cicloSelected = {
      ...cep.cicloFase.ciclo,
      confirmacaoVaga: cep.vigente
        ? {
            inicioFormatado: DateUtil.format(
              parse(cep.dtInicio, 'yyyy-MM-dd', new Date()),
              'dd/MM/yyyy',
            ),
            fimFormatado: DateUtil.format(
              parse(cep.dtFim, 'yyyy-MM-dd', new Date()),
              'dd/MM/yyyy',
            ),
            fim: cep.dtFim,
            inicio: cep.dtInicio,
          }
        : null,
    };
    this.cdr.detectChanges();
  }

  isSelectPlaceholder(controlName: string): boolean {
    const value = this.form?.get(controlName)?.value;
    return value === '' || value === null || value === undefined;
  }

  private loadCiclos(): void {
    this.loadingService.open();
    const ciclos$ = this.municipio
      ? this.chamadaEtapaPerfilService.fetchByEtapaFaseGestor(
          EtapaEnum.CONFIRMACAO_VAGA,
          FaseEnum.ADESAO_MUNICIPAL,
          this.municipio.id as string,
        )
      : this.chamadaEtapaPerfilService.fetchByEtapaFaseGestorUf(
          EtapaEnum.CONFIRMACAO_VAGA,
          FaseEnum.ADESAO_MUNICIPAL,
          this.uf?.idUfIbge as string,
        );

    ciclos$.subscribe({
        next: (response) => {
          this.loadingService.close();
          this.allChamadaEtapaPerfil = response;

          const programasMap = new Map<number, SelectOption>();
          response.forEach((cep) => {
            const prog = cep.cicloFase.ciclo.programa;
            if (!programasMap.has(prog.id)) {
              programasMap.set(prog.id, {
                label: prog.nome as string,
                value: String(prog.id),
                selected: false,
              });
            }
          });

          const programaOptions = Array.from(programasMap.values());
          this.comboProgramas.set(
            programaOptions.length === 1
              ? programaOptions
              : [{ value: '', label: 'Selecione', selected: true }, ...programaOptions],
          );

          if (programaOptions.length === 1) {
            this.form.get('programa')?.setValue(programaOptions[0].value, { emitEvent: false });
            this.form.get('programa')?.disable();
            this.onChangePrograma(programaOptions[0].value);
          }
        },
        error: (error) => {
          this.loadingService.close();
          this.alertService.error(error?.detail || 'Erro ao carregar programas. Tente novamente.');
        },
      });
  }

  private buildForm(): void {
    this.form = this.formBuilder.group({
      programa: [null, Validators.required],
      ciclo: [null, Validators.required],
    });
  }
}
